import { TeamMember } from "@/lib/sdk/team/types"
import { Nudge } from "../calendar/types"
import { ColumnDef } from "@tanstack/react-table"
import { Text } from "@chakra-ui/react"

export interface Chatbot {
  bot_type: string
  completed_reps: number
  created_at: string
  data_source: any
  data_source_count: number
  id: string
  is_active: boolean
  org_id: string
  org_name: string
  quick_bot: boolean
  raw_data_sync: boolean
  sheet_columns: number
  sheet_id: string
  sheets_row_id_support: boolean
  short_code: string
  task_count: number
  task_list: TaskProperties[]
  template: string
  title: string
  total_reps: number
  updated_at: string
  stats: ChatbotStats
  team: TeamMember[]
  reminders: Nudge[]
  qr_code: QRCode
  add_users_to_team: boolean
  changes?: TaskChanges[]
  ttl: number;
  tags: BotTag[];
  task_approvals: boolean;
  blocked: boolean;
  data_editing_enabled: boolean;
  web_channel_enabled: boolean;
  reactions_enabled: boolean;
  flows_enabled: boolean;
  batch_mode: boolean;
  enable_summary: boolean;
  /** Max plain fields per generated Flow screen. null/undefined uses the backend default of 5. */
  flow_fields_per_screen?: number | null;
  flow_id?: string;
  flow_json_hash?: string;
  flow_published?: boolean;
  flow_version?: number;
  /** Automatic syncs never publish this workflow's Flow (demos, testing a copy). */
  flow_hold_draft?: boolean;
  /** Why the last automatic sync couldn't build a Flow from the task list; null when it could. */
  flow_ineligible_reason?: string | null;
  /** The updated_at the Flow was last synced from. */
  flow_synced_at?: string | null;
  /** Saved since the Flow was last synced while automatic sync is on: launches run in chat. */
  flow_out_of_date?: boolean;
  /** When Meta last refused to open this workflow's Flow. */
  flow_launch_failed_at?: string | null;
  task_lifecycle: string;
  ttl_unit: string;
  /** Position in the org's listing. null/undefined = no assigned position, sorted last. */
  sort_order?: number | null;
  /** master_data key in the 'departments' dataset. null = unassigned. */
  department_key?: string | null;
  /** Resolved label for department_key, joined server-side. */
  department_label?: string | null;
  /** Language for bot-sent messages that support localization, e.g. extraction
   * rejection prompts. Defaults to 'en' server-side when unset. */
  language?: 'en' | 'hi';
}

export interface BotTag {
  key: string
  value: string | number
}

export interface TaskChanges {
  id: string
  name: string
  deleted: boolean
  edited: boolean
  added: boolean
}
export interface TaskProperties {
  id: string
  description: string
  name: string
  task_type: TaskType
  total_repetitions: number
  days: number
  task_actions: TaskAction[]
  task_type_metadata: string
  approval_config?: ApprovalConfig
  validation_config?: ValidationConfig
  extraction_config?: ExtractionConfig
  data_source_config?: DataSourceConfig
  startnew_config?: StartnewConfig
  auto_fill_config?: Record<string, string>
  skip_config?: SkipConfig
  counter_config?: CounterConfig
  writeback_configs?: WritebackConfig[]
  mutable: boolean
  sub_tasks?: TaskProperties[]
  soft_ttl_value?: number
  soft_ttl_unit?: string  // 'hours' | 'days'
  document_links?: string[]
  mask_output?: boolean
  require_prefill?: boolean
  tags?: BotTag[]
}

export interface TaskRep {
  id: string
  task_id: string
  chatbot_code: string
  task_name: string
  description: string
  rep_status: number
  rep_type: string
  updated_at: string
  state_changed_at: string
  source_key: string
  task_date: string
  notes: TaskNote[]
  notes_enabled_mode?: string   // 'wip' when task is in progress
  tags: BotTag[]
  soft_due_date?: string | null
  user_name?: string   // who last updated the task
  document_links?: string[]
}

/**
 * Choice option with id and display text
 */
export interface ChoiceOption {
  id: string;      // The note's xid - used for the action command
  text: string;    // The display text shown to user
}

/**
 * Extracts choice options from task notes (where is_custom_data is true)
 * @deprecated Use getChoiceOptionsWithIds instead
 */
export function getChoiceOptions(notes: TaskNote[]): string[] {
  if (!notes || !Array.isArray(notes)) {
    return [];
  }
  return notes
    .filter(note => note.is_custom_data && note.status_text)
    .map(note => note.status_text);
}

/**
 * Extracts choice options with their IDs from task notes (where is_custom_data is true)
 * Returns objects with both id (for API) and text (for display)
 */
export function getChoiceOptionsWithIds(notes: TaskNote[]): ChoiceOption[] {
  if (!notes || !Array.isArray(notes)) {
    return [];
  }
  return notes
    .filter(note => note.is_custom_data && note.status_text)
    .map(note => ({ id: note.id, text: note.status_text }));
}

export interface  UpdateStatusRequest {
  id: number
  status: number
  note?: string
}

export interface ToggleStatusRequest {
  status: boolean
}

export interface AuditMetadata {
  field_name?: string;
  old_value?: string;
  new_value?: string;
  edit_source?: 'web' | 'whatsapp';
  edited_note_id?: string;
  edited_by_name?: string;
  edited_by_mobile?: string;
}

export interface TaskNote {
  address: string;
  address_name: string;
  file_name: string;
  id: string;
  is_action: boolean;
  is_custom_data: boolean;
  is_issue: boolean;
  is_audit_log?: boolean;
  audit_metadata?: AuditMetadata;
  latitude: number;
  longitude: number;
  media_id: string;
  media_url: string;
  mime_type: string;
  mobile: string;
  name: string;
  note_id: number;
  radius: number;
  status_image?: string;
  status_text: string;
  type: string;
  updated_at: string;
}

export interface QRCode {
  code: string
  qr_image_url: string
  deep_link_url: string
  prefilled_message: string
}

export interface ChatbotStats {
  pending_tasks: number
  completed_tasks: number
}

export enum TemplateType {
  SOP = 'sop',
  FORM = 'form',
}


export interface UpdateSettingsDto {
  enable_quick_bot: boolean;
  enable_raw_data_sync: boolean;
  add_users_to_team: boolean;
  data_editing_enabled: boolean;
  web_channel_enabled: boolean;
  reactions_enabled: boolean;
  flows_enabled: boolean;
  batch_mode: boolean;
  enable_summary: boolean;
  /** Max plain fields per generated Flow screen, 1-10. null resets to the backend default of 5. */
  flow_fields_per_screen?: number | null;
  /** Automatic syncs never publish this workflow's Flow. Omitted keeps the current value. */
  flow_hold_draft?: boolean;
  /** null or '' clears the assignment. */
  department_key?: string | null;
  /** Language for bot-sent messages that support localization, e.g. extraction
   * rejection prompts. Omitted preserves the bot's current language. */
  language?: 'en' | 'hi';
}

export interface ChatbotFilters {
  orgId?: string;
  search?: string;
  offset?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  // Excludes blocked (deactivated) chatbots server-side.
  active_only?: boolean;
}

export interface TaskFilters {
  start_date ?: string;
  end_date ?: string;
}

export interface CreateChatbotExcelDto {
  template: string;
  sheet_id: string;
}

export interface CreateChatbotUIDto {
  title: string;
  template: string;
  tasks: TaskApiPayload[];
  ttl: number;
  tags: BotTag[];
  task_lifecycle?: string;
  ttl_unit?: string;
  links?: WorkflowLinks;
}

export interface UpdateChatbotExcelDto {
  template: string;
  sheet_id: string;
}

export interface UpdateChatbotUIDto {
  title: string;
  template: string;
  tasks: TaskApiPayload[];
  ttl: number;
  tags: BotTag[];
  task_lifecycle?: string;
  ttl_unit?: string;
}

export interface Task {
  id: string;
  name: string;
  description?: string;
  type: TaskType;
  data?: string;
  actions?: TaskAction[];
  approvalConfig?: ApprovalConfigUI;
  validationConfig?: ValidationConfig;
  extractionConfig?: ExtractionConfig;
  dataSourceConfig?: DataSourceConfigUI;
  startnewConfig?: StartnewConfigUI;
  autoFillConfig?: Record<string, string>;
  skipConfig?: SkipConfigUI;
  counterConfig?: CounterConfigUI;
  writebackConfigs?: WritebackConfigUI[];
  mutable: boolean;
  maskOutput?: boolean;
  requirePrefill?: boolean;
  subtasks?: Task[];
  softTtlValue?: number;
  softTtlUnit?: 'hours' | 'days';
  documentLinks?: string[];
  tags?: BotTag[];
}

// Backend API payload interface with snake_case
export interface TaskApiPayload {
  id: string;
  name: string;
  description?: string;
  type: TaskType;
  data?: string;
  actions?: TaskAction[];
  approval_config?: ApprovalConfig;
  validation_config?: ValidationConfig;
  extraction_config?: ExtractionConfig;
  data_source_config?: DataSourceConfig;
  startnew_config?: StartnewConfig;
  auto_fill_config?: Record<string, string>;
  skip_config?: SkipConfig;
  counter_config?: CounterConfig;
  writeback_configs?: WritebackConfig[];
  mutable: boolean;
  mask_output?: boolean;
  require_prefill?: boolean;
  sub_tasks?: TaskApiPayload[];
  soft_ttl_value?: number;
  soft_ttl_unit?: 'hours' | 'days';
  document_links?: string[];
  tags?: BotTag[];
}

// A named approver contact with phone and display name
export interface ApprovalContact {
  name: string;
  phone: string;
}

// One step of an approval chain (snake_case, matches http/spec/api.yaml ApprovalLevel).
// on_reject/timeout_hours/on_timeout/escalate_to_level/reject_template/parallel_group have no
// UI control yet — only `terminate` and no timeout are implemented backend-side (see
// docs/maker-checker-design.md §4.3/§4.4). They round-trip as optional fields purely so a
// config hand-authored ahead of the UI (the design doc's pilot-validation path) is never
// silently stripped by opening and resaving it through this editor.
export interface ApprovalLevel {
  level: number;
  name?: string;
  contact_type?: 'list' | 'manual';
  approval_contacts?: ApprovalContact[];
  approval_template?: string;
  reject_template?: string;
  approval_resolver?: string;
  template_builder?: ApprovalTemplateBuilder;
  quorum?: 'any' | 'all' | 'm_of_n';
  quorum_count?: number;
  timeout_hours?: number;
  on_timeout?: string;
  escalate_to_level?: number;
  on_reject?: string;
}

// One value used to fill a WhatsApp template placeholder for the 'builder' approval_resolver
// (snake_case, matches http/spec/api.yaml ApprovalVariableSource). `static`/`task_ref`/`field`
// only apply to some kinds — see docs/maker-checker-design.md for the full kind reference.
export type ApprovalVariableKind =
  | 'static'
  | 'maker_name'
  | 'task_name'
  | 'current_value'
  | 'task_value'
  | 'task_attr'
  | 'image_link'
  | 'current_upload'
  | 'generated_image'
  | 'approval_decision';

export interface ApprovalVariableSource {
  kind: ApprovalVariableKind;
  static?: string;
  task_ref?: string;
  field?: string;
  // Required when kind is 'approval_decision'. Payload-only — resolves to the same
  // task_status_ payload the built-in approve/reject buttons send, for the level/round the
  // notification is being sent for.
  decision?: 'approved' | 'rejected';
}

export interface ApprovalTemplateHeader {
  type: 'image' | 'document' | 'video' | 'text';
  source: ApprovalVariableSource;
  value_source?: ApprovalVariableSource;
}

export interface ApprovalTemplateButton {
  sub_type: 'quick_reply';
  // Ordered, concatenated with no separator — lets a payload mix literal text with a
  // resolved value, e.g. a static "/start CODE " piece followed by a task_value piece.
  payload: ApprovalVariableSource[];
  // When true, only the first tap across every recipient this button was sent to actually
  // runs the payload's action; every later tap is told it was already handled.
  unique_action?: boolean;
}

export interface ApprovalTemplateBuilder {
  header?: ApprovalTemplateHeader;
  body: ApprovalVariableSource[];
  buttons?: ApprovalTemplateButton[];
}

// ApprovalConfig interface (snake_case for backend compatibility)
export interface ApprovalConfig {
  levels?: ApprovalLevel[];
  completion_policy?: 'on_submit' | 'on_final_approval';
  max_bounces?: number;
  // Flat single-level fields. Read as the implicit level 1 when `levels` is absent; ignored by
  // the backend adapter once `levels` is present (docs/maker-checker-design.md §7.2).
  contact_type: 'list' | 'manual';
  approval_contacts?: ApprovalContact[];
  approval_template?: string;
  approval_resolver?: string;
  // Used when approval_resolver is 'builder'. Read directly here (not per-level) by the
  // one-way TEAM_ALERT/NOTIFY send (team_alert.go), which has no levels.
  template_builder?: ApprovalTemplateBuilder;
  parser_enabled?: boolean;
  parser_type?: string;
  value_source_task_id?: string;
  maker_pending_text?: string;
  maker_approved_text?: string;
  maker_rejected_text?: string;
  approver_approved_text?: string;
  approver_rejected_text?: string;
}

// Frontend ApprovalLevel (camelCase for UI components)
export interface ApprovalLevelUI {
  level: number;
  name?: string;
  contactType: 'list' | 'manual';
  approvalContacts: ApprovalContact[];
  approvalTemplate?: string;
  rejectTemplate?: string;
  approvalResolver?: string;
  templateBuilder?: ApprovalTemplateBuilderUI;
  quorum?: 'any' | 'all' | 'm_of_n';
  quorumCount?: number;
  timeoutHours?: number;
  onTimeout?: string;
  escalateToLevel?: number;
  onReject?: string;
}

// Frontend ApprovalVariableSource (camelCase for UI components)
export interface ApprovalVariableSourceUI {
  kind: ApprovalVariableKind;
  static?: string;
  taskRef?: string;
  field?: string;
  decision?: 'approved' | 'rejected';
}

export interface ApprovalTemplateHeaderUI {
  type: 'image' | 'document' | 'video' | 'text';
  source: ApprovalVariableSourceUI;
  valueSource?: ApprovalVariableSourceUI;
}

export interface ApprovalTemplateButtonUI {
  subType: 'quick_reply';
  payload: ApprovalVariableSourceUI[];
  uniqueAction?: boolean;
}

export interface ApprovalTemplateBuilderUI {
  header?: ApprovalTemplateHeaderUI;
  body: ApprovalVariableSourceUI[];
  buttons?: ApprovalTemplateButtonUI[];
}

// Frontend ApprovalConfig interface (camelCase for UI components)
export interface ApprovalConfigUI {
  levels?: ApprovalLevelUI[];
  completionPolicy?: 'on_submit' | 'on_final_approval';
  maxBounces?: number;
  contactType: 'list' | 'manual';
  approvalContacts?: ApprovalContact[];
  approvalTemplate?: string;
  approvalResolver?: string;
  templateBuilder?: ApprovalTemplateBuilderUI;
  parserEnabled?: boolean;
  parserType?: string;
  valueSourceTaskId?: string;
  makerPendingText?: string;
  makerApprovedText?: string;
  makerRejectedText?: string;
  approverApprovedText?: string;
  approverRejectedText?: string;
}

/**
 * Verification configuration for media tasks (photo/document/media).
 *
 * Runs text extraction on the upload and, by default, blocks completion when the
 * result is unusable — a blurred image, the wrong kind of document, or an unrelated
 * photo. Field names are snake_case to match the API payload exactly, so no transform
 * is needed in either direction.
 */
export interface ExtractionConfig {
  enabled: boolean;
  parser_type?: 'gov_id' | 'document' | 'pdf_extract' | 'face_present';
  /** Only applies to parser_type "gov_id". Which specific government ID is expected —
   * when set, the extracted ID number is additionally checked against that document's
   * known length/format (e.g. an Aadhaar number must be 12 digits), catching the wrong
   * document being uploaded even when its number was read correctly. Unset runs no
   * such check. */
  gov_id_type?: 'aadhaar' | 'pan' | 'passport' | 'driving_licence' | 'voter_id';
  gate?: boolean;
  required_fields?: string[];
  /** Extracted when present but never blocks the upload when missing — e.g. an address
   * on a gov ID that doesn't always carry one. */
  optional_fields?: string[];
  min_confidence?: number;
  store_result?: boolean;
  failure_message?: string;
  /** Failed extractions to allow before the upload is accepted anyway rather than
   * rejected forever. Unset means unlimited (falls back to the org default server-side). */
  max_attempts?: number;
}

// Validation configuration for number tasks
export interface ValidationConfig {
  type: 'less_than' | 'greater_than' | 'greater_than_or_equal' | 'less_than_or_equal' | 'between';
  /** For "between", the inclusive lower bound. */
  threshold?: number;
  /** Inclusive upper bound, only used when type is "between". */
  max_threshold?: number;
  reference_task?: number;
  /** Sent to the user when validation fails; the backend's built-in message is used when unset. */
  error_message?: string;
}

// Data source configuration for dynamic choice tasks (backend format with snake_case)
export interface DataSourceConfig {
  source: string;
  location_task?: number;
  region_task?: number;
  radius_km?: number;
  limit?: number;
  empty_message?: string;
  dataset?: string;
  filter_task?: number;
  lookup_field?: string;
  lookup_fields?: string[];
  match_mode?: 'exact' | 'search';
  title_field?: string;
  description_field?: string;
  mode?: string;
  answer_field?: string;
  field_filters?: Record<string, string>;
}

// Frontend format with camelCase
export interface DataSourceConfigUI {
  source: string;
  locationTask?: number;
  regionTask?: number;
  radiusKm?: number;
  limit?: number;
  emptyMessage?: string;
  dataset?: string;
  filterTask?: number;
  lookupField?: string;
  lookupFields?: string[];
  matchMode?: 'exact' | 'search';
  titleField?: string;
  descriptionField?: string;
  mode?: string;
  answerField?: string;
  /** Rows (not a map) so the editor can hold a blank row while it is filled in. */
  fieldFilters?: { field: string; value: string }[];
}

export const types = ['intro','text','number','photo','document','date','location','location_check','address','choice','dynamic_choice','regular','end','video','media','tasklist','role','counter'] as const
type TypeId = typeof types[number];

export const TaskTypeMap: Record<TypeId, string> = {
  intro: 'on_list_start',
  text: 'text_required',
  number: 'number_required',
  photo: 'photo_required',
  document: 'doc_required',
  date: 'date_required',
  location: 'loc_required',
  location_check: 'loc_check_required',
  address: 'address_required',
  choice: 'choice',
  dynamic_choice: 'dynamic_choice',
  regular: 'regular',
  video: 'video_required',
  media: 'media_required',
  tasklist: 'tasklist',
  end: 'on_list_complete',
  role: 'role',
  counter: 'counter',
} as const;

export enum TaskType {
  INTRO = 'on_list_start',
  TEXT = 'text_required',
  NUMBER = 'number_required',
  PHOTO = 'photo_required',
  DOCUMENT = 'doc_required',
  DATE = 'date_required',
  LOCATION = 'loc_required',
  LOCATION_CHECK = 'loc_check_required',
  ADDRESS = 'address_required',
  CHOICE = 'choice',
  DYNAMIC_CHOICE = 'dynamic_choice',
  REGULAR = 'regular',
  END = 'on_list_complete',
  /** @deprecated Use DOCUMENT (doc_required) + MAKERCHECKER action instead */
  INVOICE = 'invoice',
  /** @deprecated Use NUMBER (number_required) + MAKERCHECKER action instead */
  VALET = 'valet',
  VIDEO = 'video_required',
  MEDIA = 'media_required',
  TASKLIST = 'tasklist',
  ROLE = 'role',
  COUNTER = 'counter',
}

export const actions = ['SKIPPABLE','STARTNEW','MAKERCHECKER','MCQ','WIP','TEAM_ALERT','WRITEBACK'] as const
type ActionId = typeof actions[number];

export const ActionMap: Record<ActionId, string> = {
  SKIPPABLE: 'SKIP',
  STARTNEW: 'START',
  MAKERCHECKER: 'APPROVAL',
  MCQ: 'QUESTION',
  WIP: 'WIP',
  TEAM_ALERT: 'NOTIFY',
  WRITEBACK: 'WRITEBACK',
} as const;

export enum TaskAction {
  SKIPPABLE = 'SKIP',
  STARTNEW = 'START',
  MAKERCHECKER = 'APPROVAL',
  MCQ = 'QUESTION',
  WIP = 'WIP',
  TEAM_ALERT = 'NOTIFY',
  WRITEBACK = 'WRITEBACK',
}

export type ChatbotMode = 'excel' | 'ui';

export interface ChatbotFormData {
  title: string;
  template: string;
  ttl?: number;
  task_lifecycle: string;
  ttl_unit: string;
  tasks: Task[];
  tags: BotTag[];
  pendingLinks?: WorkflowLinks;
}

export interface TaskFormData {
  name: string;
  description?: string;
  type: TaskType;
  data?: string;
  actions?: TaskAction[];
  approvalConfig?: ApprovalConfigUI;
  validationConfig?: ValidationConfig;
  extractionConfig?: ExtractionConfig;
  dataSourceConfig?: DataSourceConfigUI;
  startnewConfig?: StartnewConfigUI;
  skipConfig?: SkipConfigUI;
  counterConfig?: CounterConfigUI;
  writebackConfigs?: WritebackConfigUI[];
  mutable: boolean;
  subtasks?: Task[];
  softTtlValue?: number;
  softTtlUnit?: 'hours' | 'days';
  documentLinks?: { url: string }[];
}

export interface ChatbotData {
  total: number;
  items: any[];
}

export interface TaskGroup {
  source_key: string;
  user_name: string;
  date: string;
  items: TaskData[];
}

export interface DataPoint {
  note_id: string;
  value: string;
  rep_id: number;
}


export interface TaskData {
  id: number;
  task_id: string;  // Task XID for audit trail
  name: string;
  user_name: string;
  mobile: string;
  task_date: string;
  description?: string;
  type: TaskType;
  note: string;
  note_id: string;
  Is_edited: boolean;
  rep_status: number;
  location?: {
    latitude: number;
    longitude: number;
  };
  photos?: string[];
  videos?: string[];
  documents?: string[];
  media?: string[];
  mutable: boolean;
  edit_history?: TaskNote[];  // Audit log entries showing edit history
}


export const generateFormStaticColumns = (): ColumnDef<any>[] => [
  {
    id: 'id',
    header: 'ID',
    accessorKey: 'source_key',
    enableSorting: false,
    size: 180,
  },
  {
    id: 'mobile',
    header: 'Mobile Number',
    accessorKey: 'mobile',
    size: 150,
    cell: (info: any) => {
      const phone = info.getValue();
      return phone ? `${phone}` : '-';
    },
  },
  {
    id: 'user_name',
    header: 'User Name',
    accessorKey: 'user_name',
    enableSorting: true,
    size: 180,
  },
  {
    id: 'task_date',
    header: 'Task Date',
    accessorKey: 'task_date',
    enableSorting: true,
    size: 180,
  },
  
];

export const generateFormDynamicColumns = (taskList: TaskProperties[]): ColumnDef<any>[] => {
  let filteredTaskList = taskList;
  return filteredTaskList.map(task => generateTaskColumns(task));
};

export const generateTaskColumns = (task: TaskProperties): ColumnDef<any> => ({
    id: task.id,
    header: task.name,
    accessorFn: (row) => {
      const cellData = row[task.id];
      return String(cellData?.value ?? '');
    },
    size: 200,
    enableSorting: true,
    enableResizing: true,
    cell: (info: any) => {
      const rawData = info.row.original[task.id];
      if (rawData === null || rawData === undefined || rawData.value === null || rawData.value === undefined) {
        return <Text as='i' fontSize={'sm'}>-</Text>;
      }
      return <Text as='i' fontSize={'sm'}>{`${rawData.value}`}</Text>;
    }
});


export interface DataCellProps {
    status_emoji: string
    time_completed: string
    value: string
}

// Linked Workflows Types

/**
 * Extended configuration for a linked workflow target
 * Supports per-target attribute mapping
 */
export interface WorkflowLinkConfig {
  target: string;
  attribute_mapping?: Record<string, string>;
  show_summary?: boolean;
  show_pre_complete_summary?: boolean;
}

/**
 * A workflow link value can be either:
 * - Simple: just the target workflow code (string)
 * - Extended: object with target and optional attribute_mapping
 */
export type WorkflowLinkValue = string | WorkflowLinkConfig;

/**
 * Map of link keys to target workflow codes or configurations
 * Supports both simple format ("YES": "store_order") and
 * extended format ("YES": { target: "store_order", attribute_mapping: {...} })
 */
export interface WorkflowLinks {
  [key: string]: WorkflowLinkValue;
}

/**
 * Helper to extract target code from a WorkflowLinkValue
 */
export function getTargetFromLink(value: WorkflowLinkValue): string {
  return typeof value === 'string' ? value : value.target;
}

/**
 * Helper to extract attribute mapping from a WorkflowLinkValue
 */
export function getAttributeMappingFromLink(value: WorkflowLinkValue): Record<string, string> | undefined {
  return typeof value === 'string' ? undefined : value.attribute_mapping;
}

/**
 * Helper to extract show_summary flag from a WorkflowLinkValue
 */
export function getShowSummaryFromLink(value: WorkflowLinkValue): boolean {
  return typeof value === 'string' ? false : value.show_summary ?? false;
}

/**
 * Helper to extract show_pre_complete_summary flag from a WorkflowLinkValue
 */
export function getShowPreCompleteSummaryFromLink(value: WorkflowLinkValue): boolean {
  return typeof value === 'string' ? false : value.show_pre_complete_summary ?? false;
}

/**
 * Request body for updating workflow links
 */
export interface UpdateLinksRequest {
  links: WorkflowLinks;
}

/**
 * Response from workflow links endpoints
 */
export interface LinksResponse {
  code: string;
  links: WorkflowLinks;
}

/**
 * Configuration for STARTNEW dynamic target resolution (snake_case for backend)
 */
export interface StartnewConfig {
  target_chatbot?: string;
  target_mapping_source?: string;
  target_task?: number;
  default_target_key?: string;
  attribute_mapping?: Record<string, string>;
}

/**
 * Frontend StartnewConfig interface (camelCase for UI components)
 */
export interface StartnewConfigUI {
  targetChatbot?: string;
  targetMappingSource?: string;
  targetTask?: number;
  defaultTargetKey?: string;
  attributeMapping?: Record<string, string>;
}

/**
 * Configuration for the "counter" task type (snake_case for backend)
 */
export interface CounterConfig {
  identity_task_id?: string;
  count_field?: string;
  code_task_id?: string;
  shared_identities?: string[];
}

/**
 * Frontend CounterConfig interface (camelCase for UI components)
 */
export interface CounterConfigUI {
  identityTaskId?: string;
  countField?: string;
  codeTaskId?: string;
  sharedIdentities?: string[];
}

// What a writeback policy does when no row matches: update skips, upsert adds the row,
// create always adds a row without looking one up.
export type WritebackMode = 'update' | 'upsert' | 'create';

// WritebackConfig configures the WRITEBACK task action — on completion, patches named
// fields onto a master_data row (a jsonb merge, not a replace), or adds a row, per mode. dataset and
// key_ref/keyRef are static/value-reference strings; fields maps a target master_data
// field name to a value reference — all three use the same {task_N_value} /
// {task_N_attr:field} / {attr_N} mini-language as auto_fill_config.
export interface WritebackConfig {
  dataset: string;
  key_ref: string;
  // Master_data field key_ref's resolved value is matched against — "key" or
  // "label" match those columns, any other string is a custom field name.
  // Undefined defaults to "key".
  match_field?: string;
  // Undefined defaults to "update".
  mode?: WritebackMode;
  // Value references for an added row's label and parent_key.
  label_ref?: string;
  parent_key_ref?: string;
  fields: Record<string, string>;
}

/**
 * Frontend WritebackConfig interface (camelCase for UI components)
 */
export interface WritebackConfigUI {
  dataset: string;
  keyRef: string;
  matchField?: string;
  mode?: WritebackMode;
  labelRef?: string;
  parentKeyRef?: string;
  fields: Record<string, string>;
}

/**
 * Skip condition operators for conditional skip logic
 * String operators: equals, not_equals, contains, not_contains, empty, not_empty
 * Numeric operators (for number_required tasks): less_than, less_than_or_equal, greater_than, greater_than_or_equal
 */
export type SkipConditionOperator =
  | 'equals'
  | 'not_equals'
  | 'contains'
  | 'not_contains'
  | 'empty'
  | 'not_empty'
  | 'less_than'
  | 'less_than_or_equal'
  | 'greater_than'
  | 'greater_than_or_equal';

/**
 * Skip mode for conditional skip
 * - auto: Task is automatically skipped when condition is met (no user interaction)
 * - manual: Skip button is shown only when condition is met (user chooses)
 */
export type SkipMode = 'auto' | 'manual';

/**
 * How multiple skip conditions combine
 * - and: every condition must be met
 * - or: at least one condition must be met
 */
export type SkipConditionLogic = 'and' | 'or';

/**
 * A single skip condition (backend format with snake_case)
 */
export interface SkipCondition {
  condition_task: number;
  condition_operator?: SkipConditionOperator;
  condition_value?: string;
  condition_value_task?: number;
}

/**
 * Skip configuration (backend format with snake_case)
 * The top-level condition_* fields are deprecated in favour of conditions[]; they are
 * still read by the backend so task lists saved before multi-condition support work.
 */
export interface SkipConfig {
  condition_task?: number;
  condition_operator?: SkipConditionOperator;
  condition_value?: string;
  condition_value_task?: number;
  conditions?: SkipCondition[];
  condition_logic?: SkipConditionLogic;
  skip_mode?: SkipMode;
}

/**
 * A single skip condition (camelCase for UI components)
 */
export interface SkipConditionUI {
  conditionTask?: number;
  conditionOperator?: SkipConditionOperator;
  conditionValue?: string;
  conditionValueTask?: number;
}

/**
 * Frontend SkipConfig interface (camelCase for UI components)
 */
export interface SkipConfigUI {
  conditions?: SkipConditionUI[];
  conditionLogic?: SkipConditionLogic;
  skipMode?: SkipMode;
}